import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Grid, Tab, Tabs } from '@material-ui/core';
import { PostAdd } from '@material-ui/icons';
import qs from 'qs';
import { identity, pickBy } from 'lodash';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import { AxiosError } from 'axios';
import { useQuery } from 'hooks/useQuery';
import PageHeader from 'components/PageHeader';
import { Article, CreateArticle, Menu, Search } from './components';
import { Confluence } from './interfaces';
import { parseSection, SECTION_LABELS } from './helpers';
import { loadConfluenceArticle, loadConfluenceMenu, searchConfluenceMenu } from './api';

export const path = '/communications';

interface IProps {}

const PageConfluence: React.FC<IProps> = () => {
  const history = useHistory();
  const params = useQuery();
  const operations = useSelector((state: RootState) => state.operations);

  const articleId = params.get('articleId');
  const search = params.get('search');
  const section = parseSection(params.get('section'));

  const [article, setArticle] = useState<{
    article: Confluence.IArticleBase | null;
    menu: Confluence.IMenu;
    loading: boolean;
    error: string | null;
  }>({ article: null, menu: [], loading: false, error: null });

  const [menu, setMenu] = useState<Map<string, { data: Confluence.IMenu; loading: boolean; error: string | null }>>(
    new Map(),
  );

  // меню кэшируется по паре «раздел + строка поиска», чтобы разделы не смешивались
  const menuKey = (sectionValue: Confluence.ESections, text: string) => `${sectionValue}|${text}`;

  const handleLoadArticle = useCallback(
    async (id: number, hasHtml?: boolean, resetSearch?: boolean, sectionValue: Confluence.ESections = section) => {
      try {
        setArticle((article) => ({ ...article, loading: true, error: null }));
        history.push({
          search: qs.stringify(
            pickBy({ section: sectionValue, articleId: id, search: resetSearch ? undefined : search }, identity),
          ),
        });

        const article = await loadConfluenceArticle(id);
        setArticle((prevArticle) => ({ ...prevArticle, article, error: null }));

        if (!hasHtml || !article.html) {
          const menu = await loadConfluenceMenu({ params: { id } });
          setArticle((article) => ({ ...article, menu }));
        }
      } catch (error) {
        setArticle((article) => ({
          ...article,
          loading: false,
          error: (error as AxiosError).response!.data.message,
        }));
      } finally {
        setArticle((article) => ({ ...article, loading: false }));
      }
    },
    [search, section], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleLoadMenu = useCallback(
    async (text: string = '', force: boolean = false, sectionValue: Confluence.ESections = section) => {
      const key = menuKey(sectionValue, text);
      if (menu.has(key) && !force) return;

      const updateMenu = (newData: Partial<{ data: Confluence.IMenu; loading: boolean; error: string | null }>) => {
        setMenu((prevMenu) => {
          const updatedMenu = new Map(prevMenu);

          updatedMenu.set(key, {
            data: newData.data ?? prevMenu.get(key)?.data ?? [],
            loading: newData.loading!,
            error: newData.error!,
          });
          return updatedMenu;
        });
      };

      try {
        updateMenu({ loading: true, error: null });

        const data = await (text
          ? searchConfluenceMenu({ params: { text, section: sectionValue } })
          : loadConfluenceMenu({ params: { section: sectionValue } }));
        updateMenu({ data, loading: false, error: null });
      } catch (error) {
        updateMenu({ loading: false, error: (error as AxiosError).response?.data.messag });
      }
    },
    [menu, section], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const canManageArticles = [USER_OPERATIONS.CONFLUENCE_MANAGER, USER_OPERATIONS.CONFLUENCE_LESSON_MANAGER].some(
    (operation) => operations.includes(operation),
  );

  const handleChangeSection = (nextSection: Confluence.ESections) => {
    if (nextSection === section) return;

    setArticle({ article: null, menu: [], loading: false, error: null });
    history.push({ search: qs.stringify({ section: nextSection }) });
  };

  useEffect(() => {
    handleLoadMenu(search || '');
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (articleId) handleLoadArticle(+articleId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ maxWidth: 1565, padding: 20, margin: '0 auto' }}>
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <PageHeader title="Книга Знаний Сервиса" isBold />

          <Tabs
            value={section}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_, value) => handleChangeSection(value as Confluence.ESections)}
          >
            {Array.from(SECTION_LABELS).map(([value, label]) => (
              <Tab key={value} value={value} label={label} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
          {canManageArticles && (
            <Box sx={{ mr: 1 }}>
              <CreateArticle onCreate={() => handleLoadMenu('', true)}>
                {(onOpen) => (
                  <Button variant="outlined" endIcon={<PostAdd />} onClick={onOpen}>
                    Создать раздел
                  </Button>
                )}
              </CreateArticle>
            </Box>
          )}

          <Search onSearch={(text) => handleLoadMenu(text)} />
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item lg={3} md={3} sm={4} xs={12}>
          <Box sx={{ maxHeight: 'calc(100vh - 202px)', minHeight: 200, overflow: 'auto' }}>
            <Menu
              menu={menu.get(menuKey(section, search || ''))}
              onSelect={(menuItem) => handleLoadArticle(menuItem.menuId, menuItem.hasHtml)}
              onCreate={() => handleLoadMenu('', true)}
            />
          </Box>
        </Grid>

        <Grid item lg={9} md={9} sm={8} xs={12}>
          <Article
            article={article}
            menu={menu.get(menuKey(section, ''))?.data || []}
            onDelete={(article) => {
              article.id && handleLoadArticle(article.id);
              handleLoadMenu('', true);
              history.push({ pathname: path, search: qs.stringify({ section }) });
            }}
            onMove={(article, targetSection) => {
              // перенос меняет деревья обоих разделов — сбрасываем кэш меню целиком
              setMenu(new Map());
              handleLoadMenu('', true, targetSection);
              article.id && handleLoadArticle(article.id, undefined, true, targetSection);
            }}
            onClone={(cloneId, targetSection) => {
              // копия появляется в дереве целевого раздела — сбрасываем кэш меню и открываем её
              setMenu(new Map());
              handleLoadMenu('', true, targetSection);
              handleLoadArticle(cloneId, undefined, true, targetSection);
            }}
            onShowOrHide={(article) => {
              article.id && handleLoadArticle(article.id);
              handleLoadMenu('', true);
            }}
            onUpdate={(article) => {
              article.id && handleLoadArticle(article.id);
              handleLoadMenu('', true);
            }}
            onSelectMenuItem={(menuItem) => {
              // переход по хлебным крошкам/вложенным статьям выводит из режима поиска:
              // сбрасываем строку поиска и показываем обычное дерево меню
              handleLoadArticle(menuItem.menuId, undefined, true);
              handleLoadMenu('');
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PageConfluence;
